/**
 * HeatReport Styled Components
 * Replaces makeStyles with styled components for MUI v6
 */

import { Box, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";

/**
 * Replaces classes.borderAgGridTable
 * Used for wrapping ag-grid tables with border and padding
 */
export const BorderedTablePaper = styled(Paper)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px",
  padding: "10px",
}));

/**
 * Replaces classes.outlinedTextarea
 * Used for textarea elements with outlined style
 */
export const OutlinedTextarea = styled("textarea")(({ theme }) => ({
  width: "100%",
  padding: "10px 12px",
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: theme.shape.borderRadius,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.fontSize,
  transition: theme.transitions.create(["border-color", "box-shadow"]),
  "&:hover": {
    borderColor: theme.palette.text.primary,
  },
  "&:focus": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.light}`,
    outline: "none",
  },
  "&::placeholder": {
    color: theme.palette.text.secondary,
    opacity: 1,
  },
}));

/**
 * Replaces classes.assetTitle
 * Used for asset card titles
 */
export const AssetTitleBox = styled(Box)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  userSelect: "none",
  height: "100%",
}));

/**
 * Container for field groups with consistent spacing
 * Replaces classes.container
 */
export const FieldContainer = styled(Box)(() => ({
  display: "flex",
  flexWrap: "nowrap",
  width: "100%",
}));

/**
 * Totals row for tables
 * Replaces classes.totalsRow
 */
export const TotalsRow = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  marginTop: theme.spacing(1.5),
}));

/**
 * Individual total cell
 * Replaces classes.totalCell
 */
export const TotalCell = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(0.5),
  fontWeight: "bold",
}));

/**
 * Dynamic height container for ag-grid tables
 * @param rowCount - Number of rows to display
 * @param minRows - Minimum number of rows (default: 5)
 */
interface DynamicHeightContainerProps {
  rowCount?: number;
  minRows?: number;
}

const GRID_DIMENSIONS = {
  HEADER_HEIGHT: 40,
  ROW_HEIGHT: 30,
};

export const DynamicHeightContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "rowCount" && prop !== "minRows",
})<DynamicHeightContainerProps>(({ rowCount = 5, minRows = 5 }) => {
  const actualRowCount = Math.max(rowCount, minRows);
  const PADDING = 100;
  const calculatedHeight =
    GRID_DIMENSIONS.HEADER_HEIGHT +
    actualRowCount * GRID_DIMENSIONS.ROW_HEIGHT +
    PADDING;

  return {
    padding: "0px 0px 50px 0px",
    height: `${calculatedHeight}px`,
  };
});

/**
 * Grouped text fields - removes border between adjacent fields
 * Used for fields that are visually connected (e.g., time ranges)
 */
import { Card, Tab, Tabs, TextField } from "@mui/material";

/**
 * Left field in a grouped pair - removes right border
 */
export const LeftGroupedTextField = styled(TextField)(() => ({
  flex: 1,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderRight: "none",
  },
}));

/**
 * Right field in a grouped pair - removes left border radius
 */
export const RightGroupedTextField = styled(TextField)(() => ({
  flex: 1,
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
}));

/**
 * Styled Tabs component for file navigation
 * Replaces classes.fileTabsRoot and classes.fileTabsIndicator
 */
export const StyledTabs = styled(Tabs)(() => ({
  background: "transparent",
  minHeight: 0,
  borderBottom: "none",
  "& .MuiTabs-indicator": {
    display: "none",
  },
}));

/**
 * Styled Tab component for file navigation
 * Replaces classes.fileTabRoot and classes.fileTabSelected
 */
export const StyledTab = styled(Tab)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.divider}`,
  borderBottom: "none",
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  textTransform: "none",
  minHeight: 60,
  fontSize: 20,
  padding: theme.spacing(1, 2),
  marginRight: theme.spacing(1),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  "&.Mui-selected": {
    backgroundColor: theme.palette.background.paper,
    fontWeight: theme.typography.fontWeightMedium,
  },
}));

/**
 * Styled Card component for asset titles
 * Replaces classes.assetTitle with Card styling
 */
export const AssetTitleCard = styled(Card)(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  userSelect: "none",
  height: "100%",
}));
